import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { ANALYTICS_READY } from '@/consent/script';

declare global {
  interface Window {
    /** Defined by the consent script only after Accept, on the production hostnames. */
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * What a route reports as, to analytics (TW-7): a fixed title and a path with nothing personal in it. Never the tab
 * title, which carries the countdown, and never the real URL, which can hold an invite token or an OAuth query.
 */
const PAGES: { match: RegExp; title: string; path: string }[] = [
  { match: /^\/$/, title: 'Home', path: '/' },
  { match: /^\/timer/, title: 'Timer', path: '/timer' },
  { match: /^\/sessions/, title: 'Sessions', path: '/sessions' },
  { match: /^\/projects/, title: 'Projects', path: '/projects' },
  { match: /^\/team/, title: 'Team', path: '/team' },
  { match: /^\/settings/, title: 'Settings', path: '/settings' },
  { match: /^\/auth/, title: 'Sign in', path: '/auth' },
  { match: /^\/join\//, title: 'Join a team', path: '/join' },
];

export function pageFor(pathname: string): { title: string; path: string } {
  const page = PAGES.find((p) => p.match.test(pathname));
  return { title: `${page?.title ?? 'Other'} - Timato`, path: page?.path ?? '/other' };
}

/** One page view for this route. Also sets the title and location for the tag's other events (engagement, scroll). */
export function sendPageView(pathname: string) {
  if (!window.gtag) return; // no consent, not production, or no measurement id
  const { title, path } = pageFor(pathname);
  const params = { page_title: title, page_location: `${location.origin}${path}` };
  window.gtag('set', params);
  window.gtag('event', 'page_view', params);
}

/** Mounted once at the router root: a page view on every route change, and one when consent arrives mid-visit. */
export function usePageViews() {
  const { pathname } = useLocation();
  useEffect(() => {
    sendPageView(pathname);
    const ready = () => sendPageView(pathname);
    window.addEventListener(ANALYTICS_READY, ready);
    return () => window.removeEventListener(ANALYTICS_READY, ready);
  }, [pathname]);
}
